/**
 * Technology types for sequencing run library assignments.
 * Used as discriminator for polymorphic union types.
 */
export type RunLibraryTechnology =
  | 'NGS'
  | 'MICROARREGLOS'
  | 'MLPA'
  | 'EXPANSION_TRIPLETAS';

/**
 * Specimen within a run library assignment.
 * Contains LG code and index information.
 */
export interface RunLibrarySpecimen {
  /** Unique specimen assignment identifier */
  readonly libSpecimenId: string;

  /** Specimen identifier */
  readonly specimenId: string;

  /** Index adapter identifier */
  readonly indexAdapterId: string;

  /** Index adapter value (e.g., UDP0001) */
  readonly indexValue: string;

  /** LG code for the specimen */
  readonly lgCode: string;

  /** Date when specimen was assigned */
  readonly assignmentDate: string;
}

/**
 * Base interface for run library assignments.
 * Contains common fields shared by all technology types.
 *
 * Following Interface Segregation Principle: each technology-specific
 * interface only contains the fields relevant to that technology.
 */
export interface BaseRunLibraryAssignment {
  /** Technology discriminator for type narrowing */
  readonly technology: RunLibraryTechnology;

  /** Run library assignment ID */
  readonly runLibId: string;

  /** Library ID */
  readonly libraryId: string;

  /** Library code (e.g., "LIB001") */
  readonly libraryCode: string;

  /** Replica column number (1-4) */
  readonly replicaColumn: number;

  /** Line number within the column (1-32) */
  readonly lineNumber: number;

  /** Assignment timestamp */
  readonly assignmentDate: string;

  /** Specimens assigned to this library with LG codes */
  readonly specimens: RunLibrarySpecimen[];

  /** Creation timestamp for the library */
  readonly createdAt: string;
}

/**
 * NGS-specific run library assignment.
 * Contains only the common fields - NGS has no additional metadata.
 */
export interface NgsRunLibraryAssignment extends BaseRunLibraryAssignment {
  readonly technology: 'NGS';
}

/**
 * Microarray-specific run library assignment.
 * Contains chip metadata: chipType, arrangementType, plateName, chipLot, chipReference.
 */
export interface MicroarrayRunLibraryAssignment
  extends BaseRunLibraryAssignment {
  readonly technology: 'MICROARREGLOS';

  /** Chip type for microarray libraries (e.g., "CytoScan HD") */
  readonly chipType: string;

  /** Arrangement type for microarray libraries */
  readonly arrangementType: string;

  /** Plate name for microarray libraries */
  readonly plateName: string | null;

  /** Chip lot number */
  readonly chipLot: string | null;

  /** Chip reference */
  readonly chipReference: string | null;
}

/**
 * MLPA-specific run library assignment.
 * Contains MLPA metadata: equipment, plateName, creationDate, batchLot, batchReference.
 */
export interface MlpaRunLibraryAssignment extends BaseRunLibraryAssignment {
  readonly technology: 'MLPA';

  /** Equipment used for library creation (e.g., "SeqStudio") */
  readonly equipment: string | null;

  /** Plate name for MLPA libraries */
  readonly plateName: string | null;

  /** Creation date for the library (format: YYYY-MM-DD) */
  readonly creationDate: string | null;

  /** Batch lot number */
  readonly batchLot: string | null;

  /** Batch reference */
  readonly batchReference: string | null;
}

/**
 * Triplet Expansion-specific run library assignment.
 * Contains same metadata as MLPA: equipment, plateName, creationDate, batchLot, batchReference.
 */
export interface TripletExpansionRunLibraryAssignment
  extends BaseRunLibraryAssignment {
  readonly technology: 'EXPANSION_TRIPLETAS';

  /** Equipment used for library creation (e.g., "SeqStudio") */
  readonly equipment: string | null;

  /** Plate name for Triplet Expansion libraries */
  readonly plateName: string | null;

  /** Creation date for the library (format: YYYY-MM-DD) */
  readonly creationDate: string | null;

  /** Batch lot number */
  readonly batchLot: string | null;

  /** Batch reference */
  readonly batchReference: string | null;
}

/**
 * Polymorphic union type for run library assignments.
 * TypeScript will narrow the type based on the 'technology' discriminator.
 *
 * Example usage:
 * ```typescript
 * function processLibrary(lib: RunLibraryAssignment) {
 *   if (lib.technology === 'MLPA') {
 *     // TypeScript knows lib is MlpaRunLibraryAssignment here
 *     console.log(lib.equipment); // ✅ No error
 *   }
 * }
 * ```
 */
export type RunLibraryAssignment =
  | NgsRunLibraryAssignment
  | MicroarrayRunLibraryAssignment
  | MlpaRunLibraryAssignment
  | TripletExpansionRunLibraryAssignment;

// ============================================
// TYPE GUARDS for runtime type checking
// ============================================

/**
 * Type guard to check if a library assignment is NGS.
 */
export function isNgsLibraryAssignment(
  lib: RunLibraryAssignment
): lib is NgsRunLibraryAssignment {
  return lib.technology === 'NGS';
}

/**
 * Type guard to check if a library assignment is Microarray.
 */
export function isMicroarrayLibraryAssignment(
  lib: RunLibraryAssignment
): lib is MicroarrayRunLibraryAssignment {
  return lib.technology === 'MICROARREGLOS';
}

/**
 * Type guard to check if a library assignment is MLPA.
 */
export function isMlpaLibraryAssignment(
  lib: RunLibraryAssignment
): lib is MlpaRunLibraryAssignment {
  return lib.technology === 'MLPA';
}

/**
 * Type guard to check if a library assignment is Triplet Expansion.
 */
export function isTripletExpansionLibraryAssignment(
  lib: RunLibraryAssignment
): lib is TripletExpansionRunLibraryAssignment {
  return lib.technology === 'EXPANSION_TRIPLETAS';
}

/**
 * Type guard to check if a library has MLPA-like metadata (MLPA or Triplet Expansion).
 */
export function hasMlpaMetadata(
  lib: RunLibraryAssignment
): lib is MlpaRunLibraryAssignment | TripletExpansionRunLibraryAssignment {
  return lib.technology === 'MLPA' || lib.technology === 'EXPANSION_TRIPLETAS';
}

/**
 * Type guard to check if a library has chip metadata (Microarray).
 */
export function hasChipMetadata(
  lib: RunLibraryAssignment
): lib is MicroarrayRunLibraryAssignment {
  return lib.technology === 'MICROARREGLOS';
}
